/*
 * @Author: Tahiti
 * @Date: 2025-10-23 11:15:53
 * @LastEditors: Tahiti
 * @LastEditTime: 2025-12-09 10:07:37
 * @Description:
 * @FilePath: /weryai-web/stores/create.ts
 */
/**
 * 用户状态管理
 * 管理用户登录信息、token、用户资料等
 */
import { defineStore } from 'pinia'
import {
    createImageApi,
    getTaskStatusApi,
    getTaskResultDetailApi,
    getActionCreditRequiredApi,
    createImageToImageApi,
    createTextToVideoApi,
    createImageToVideoSingleApi,
    createImageToVideoMultipleApi,
    createImageToVideoFirstLastFrameApi,
    createAlmightyReferenceToVideoApi,
    createVideoEditApi,
    createBatchGenerateTxtToImageApi,
    createBatchGenerateImgToImageApi,
    getNewActionCreditRequiredApi,
} from '~/api/create'
import type {
    CreateImageData,
    TaskStatusResponse,
    PollingOptions,
    CreateType,
} from '~/types'
import { pollTaskStatus, TaskPoller } from '~/utils/polling'
import { useUserStore } from '~/stores/user'
import { noticeMessagePopup, asleep } from '~/utils/common'
import { showLoginModal, emitOpenGoodsModal, emitter } from '~/utils/emitter'
import { useAppStore } from '~/stores/app'
import { messages } from '~/locales'
import { useI18n } from '~/composables/useI18n'
const handleCheckImageToVideoType = (type: string) => {
    switch (type) {
        case 'single_to_video':
            return createImageToVideoSingleApi
        case 'multi_to_video':
            return createImageToVideoMultipleApi
        case 'fist_last_to_video':
            return createImageToVideoFirstLastFrameApi
        default:
            console.warn(`未知的图片转视频类型: ${type}`)
            return createImageToVideoSingleApi // 默认返回单图转视频API
    }
}

const handleCheckCreateType = (create_type: string, type: string): any => {
    switch (create_type) {
        case 'TEXT_TO_IMG_BATCH':
            return createBatchGenerateTxtToImageApi
        case 'IMG_EDIT_BATCH':
            return createBatchGenerateImgToImageApi
        case 'text_to_image':
            return createImageApi
        case 'image_to_image':
            return createImageToImageApi
        case 'image_to_video':
            return handleCheckImageToVideoType(type)
        case 'text_to_video':
            return createTextToVideoApi
        case 'video_edit':
            return createVideoEditApi
        default:
            console.error(`未知的创建类型: ${create_type}`)
            throw new Error(`不支持的创建类型: ${create_type}`)
    }
}

interface CreateState {
    record_id: number | null
    task_id: number | null
    create_type: string | null
    current_poller: TaskPoller | null
    polling_tasks: Map<string, TaskPoller> // 存储多个轮询任务
    result_detail: any | null
    credit_required_data: Record<string, any> // 当前操作所需积分
    select_img_list: string[]
    select_video_list: string[]
}

/**
 * 用户状态管理 Store
 * 使用 Pinia 进行状态管理，提供响应式的用户状态
 */
export const useCreateStore = defineStore('create', {
    /**
     * 初始状态定义
     */
    state: (): CreateState => ({
        record_id: null,
        task_id: null,
        create_type: null,
        current_poller: null,
        polling_tasks: new Map(),
        result_detail: null,
        credit_required_data: {},
        select_img_list: [],
        select_video_list: [],
    }),

    /**
     * 计算属性
     */
    getters: {
        current_log_id: state => state.record_id,
        current_task_id: state => state.task_id,
        current_create_type: state => state.create_type,
        is_polling: state => state.current_poller?.polling || false,
        polling_tasks_count: state => state.polling_tasks.size,
        record_result_detail: state => state.result_detail,
        cost_credit_data: state => state.credit_required_data,
        edit_img_list: state => state.select_img_list,
        edit_video_list: state => state.select_video_list,
    },

    /**
     * 状态操作方法
     */
    actions: {
        // 当前类型的操作所需积分
        async getCreditRequiredData(params: any) {
            let res: any = null
            try {
                res = await getNewActionCreditRequiredApi(params)
                console.log('res', res)
                if (res.status === 200) {
                    this.credit_required_data[params.workflowType] = {
                        credits: res.data,
                        workflowType: params.workflowType,
                    }
                } else {
                    // API 返回错误时，设置默认值为 0
                    this.credit_required_data[params.workflowType] = {
                        credits: 0,
                        workflowType: params.workflowType,
                    }
                }
            } catch (error) {
                // 捕获错误（如 workflowType 不存在），设置默认值为 0，不显示错误弹窗

                this.credit_required_data[params.workflowType] = {
                    credits: 0,
                    workflowType: params.workflowType,
                }
            } finally {
                return res
            }
        },

        /**
         * @description: 设置创建类型
         * @param {string} create_type
         * @return {*}
         */
        setCreateType(create_type: string) {
            this.create_type = create_type
        },
        /**
         * @description: 获取任务状态
         * @param {string} task_id
         * @return {*}
         */
        async getTaskStatus(task_id: string) {
            const res: any = await getTaskStatusApi({ task_id })
            return res.data
        },

        /**
         * @description: 获取任务生成结果详情
         * @param {string} id
         * @return {*}
         */
        async getTaskResultDetail(id: number) {
            const res: any = await getTaskResultDetailApi({ id })
            if (res.status === 200) {
                this.result_detail = res.data
            }
        },

        /**
         * @description: 文本/图片生成图片
         * @param {CreateImageData} data
         * @return {*}
         */
        async handleCreateImageOrVideo(data: CreateImageData) {
            // 使用 getLocalName 工具函数获取翻译（适用于 store 环境）
            const { locale } = useI18n()

            // 使用 getCachedMessages 安全获取已缓存的消息（同步）
            const { getCachedMessages } = await import('~/locales')
            const localeMessages =
                getCachedMessages(locale.value as any) ||
                getCachedMessages('en')
            const message_name = localeMessages?.common?.oneTaskRunning
            const message_tip = localeMessages?.common?.tip
            let res: any = null
            try {
                const createApi =
                    data.create_type === 'image_to_video' &&
                    data.type === 'multi_to_video' &&
                    ((data as any).mode === 'ALMIGHTY_REFERENCE' ||
                        (data as any).credit_workflow_key ===
                            'almightyReferenceToVideo')
                        ? createAlmightyReferenceToVideoApi
                        : handleCheckCreateType(
                              data.create_type as string,
                              data.type as any
                          )

                res = await createApi(data)
                if (res.status === 200) {
                    noticeMessagePopup(
                        message_name,
                        message_tip,
                        'success',
                        5000,
                        ''
                    )
                    const { log_id, task_id } = res.data

                    this.record_id = log_id
                    this.task_id = task_id
                    this.create_type = data.create_type as string
                    if (
                        [
                            'IMG_EDIT_BATCH',
                            'TEXT_TO_IMG_BATCH',
                            'text_to_video',
                            'image_to_video',
                            'video_edit',
                        ].includes(data.create_type as string)
                    ) {
                        await asleep(2000)
                        // 提交任务成功后，触发事件刷新创作历史记录
                        emitter.emit('refresh-creation-history')
                    } else {
                        // 当前页面URL后面添加record_id
                        const url = location.href
                        const new_url = new URL(url)
                        new_url.searchParams.set('record_id', log_id)
                        window.history.replaceState({}, '', new_url.toString())
                    }
                    await asleep(500)
                    const appStore = useAppStore()
                    appStore.getProjectList()
                } else if ([1005, 4001, 4002].includes(res.status)) {
                    showLoginModal()
                    return
                }
            } catch (error) {
                console.error('创建失败:', error)
            } finally {
                // 更新余额
                useUserStore().getBalance()
            }
        },

        /**
         * @description: 通用轮询任务状态方法
         * @param {string} taskId 任务ID
         * @param {PollingOptions} options 轮询配置选项
         * @return {Promise<TaskStatusResponse>}
         */
        async pollTaskStatus(
            taskId: string,
            options: PollingOptions = {}
        ): Promise<TaskStatusResponse> {
            return pollTaskStatus(
                taskId,
                this.getTaskStatus.bind(this),
                options
            )
        },

        /**
         * @description: 创建轮询器并开始轮询
         * @param {string} taskId 任务ID
         * @param {PollingOptions} options 轮询配置选项
         * @return {Promise<TaskStatusResponse>}
         */
        async startPolling(
            taskId: string,
            options: PollingOptions = {}
        ): Promise<TaskStatusResponse> {
            // 停止当前轮询
            if (this.current_poller) {
                this.current_poller.stop()
            }

            // 创建新的轮询器
            const poller = new TaskPoller(
                taskId,
                this.getTaskStatus.bind(this),
                options
            )
            this.current_poller = poller

            // 将轮询器添加到任务映射中
            this.polling_tasks.set(taskId, poller)

            try {
                const result = await poller.start()
                return result
            } finally {
                // 轮询完成后清理
                this.polling_tasks.delete(taskId)
                if (this.current_poller === poller) {
                    this.current_poller = null
                }
            }
        },

        /**
         * @description: 停止指定任务的轮询
         * @param {string} taskId 任务ID
         * @return {void}
         */
        stopPolling(taskId: string): void {
            const poller = this.polling_tasks.get(taskId)
            if (poller) {
                poller.stop()
                this.polling_tasks.delete(taskId)
            }

            // 如果是当前轮询器，清空
            if (this.current_poller && this.current_poller === poller) {
                this.current_poller = null
            }
        },

        /**
         * @description: 停止所有轮询任务
         * @return {void}
         */
        stopAllPolling(): void {
            // 停止所有轮询任务
            this.polling_tasks.forEach(poller => {
                poller.stop()
            })
            this.polling_tasks.clear()

            // 停止当前轮询器
            if (this.current_poller) {
                this.current_poller.stop()
                this.current_poller = null
            }
        },

        /**
         * @description: 创建并轮询任务（一步完成）
         * @param {CreateImageData} data 创建数据
         * @param {PollingOptions} options 轮询配置选项
         * @return {Promise<TaskStatusResponse>}
         */
        async createAndPoll(
            data: CreateImageData,
            options: PollingOptions = {}
        ): Promise<TaskStatusResponse> {
            // 先创建任务
            await this.handleCreateImageOrVideo(data)

            if (!this.task_id) {
                throw new Error('task create failed, task_id is null')
            }

            // 开始轮询
            return this.startPolling(this.task_id.toString(), options)
        },

        /**
         * @description: 获取指定任务的轮询状态
         * @param {string} taskId 任务ID
         * @return {boolean}
         */
        isTaskPolling(taskId: string): boolean {
            const poller = this.polling_tasks.get(taskId)
            return poller ? poller.polling : false
        },

        /**
         * @description: 获取指定任务的尝试次数
         * @param {string} taskId 任务ID
         * @return {number}
         */
        getTaskAttempts(taskId: string): number {
            const poller = this.polling_tasks.get(taskId)
            return poller ? poller.currentAttempts : 0
        },

        /**
         * @description: 设置选中的图片列表
         * @param {any[]} list - 图片列表，可以是对象数组 {url: string, type: string} 或字符串数组
         * @return {void}
         */
        setSelectImgList(list: any[]): void {
            this.select_img_list = list
                .filter(item => item.type === 'IMAGE')
                .map(item => item.url)
            this.select_video_list = list
                .filter(item => item.type === 'VIDEO')
                .map(item => item.url)
        },
    },
})
